import { computed, effect, inject, signal, untracked } from '@angular/core'
import { FormGroup, ValidationErrors } from '@angular/forms'
import { toSignal } from '@angular/core/rxjs-interop'
import { firstValueFrom, catchError, of, map, startWith } from 'rxjs'
import { LocationService, City, Street } from '../../shared/services/location.service'
import { useInputRestrictions } from './use-input-restrictions'
import { ContactFormControls } from '../../shared/models/main-form'

export function useAddressLogic(formGroup: FormGroup<ContactFormControls>) {
  const locationService = inject(LocationService)
  const inputRestrictions = useInputRestrictions()

  // Signals
  const postalCodeSignal = signal<string>('', { equal: () => false })
  const citiesSignal = signal<string[]>([])
  const streetsSignal = signal<string[]>([])
  const selectedCitySignal = signal<string>('')
  const cityLookupCompletedPostalCodeSignal = signal<string>('')
  const pendingExternalValues = signal<{ city: string; street: string } | null>(null)
  // Fields filled by the Commercial Register are locked individually; unlocked fields keep the usual lookup behaviour
  const lockedFields = signal({ postalCode: false, city: false, street: false, houseNumber: false })
  const hasAnyLockedField = () => Object.values(lockedFields()).some(Boolean)

  // Readonly state tracking
  const isCityReadonly = signal<boolean>(false)
  const isStreetReadonly = signal<boolean>(false)

  // Computed values for display logic
  const shouldShowCityDropdown = computed(() => {
    const cities = citiesSignal()
    return cities.length > 1
  })

  const shouldShowCityInput = computed(() => {
    const cities = citiesSignal()
    return cities.length <= 1
  })

  const shouldShowStreetDropdown = computed(() => {
    const streets = streetsSignal()
    return streets.length > 1
  })

  const shouldShowStreetInput = computed(() => {
    const streets = streetsSignal()
    return streets.length <= 1
  })

  // Initially disable the city and street fields
  formGroup.controls.addressCity.disable()
  formGroup.controls.addressStreet.disable()

  // Keep backend postal code validation stable across blur/update cycles.
  formGroup.controls.addressPostalCode.addValidators((): ValidationErrors | null => {
    const postalCode = formGroup.controls.addressPostalCode.value || ''

    if (postalCode.length !== 5) {
      return null
    }

    if (cityLookupCompletedPostalCodeSignal() !== postalCode) {
      return null
    }

    return citiesSignal().length === 0 ? { noCitiesFound: true } : null
  })

  effect(() => {
    citiesSignal()
    cityLookupCompletedPostalCodeSignal()
    formGroup.controls.addressPostalCode.updateValueAndValidity({ emitEvent: false })
  })

  // Create a reactive signal from the city form control value changes
  const cityValueSignal = toSignal(
    formGroup.controls.addressCity.valueChanges.pipe(startWith(formGroup.controls.addressCity.value)),
    { initialValue: '' }
  )

  // Create a reactive signal from the street form control value changes
  const streetValueSignal = toSignal(
    formGroup.controls.addressStreet.valueChanges.pipe(startWith(formGroup.controls.addressStreet.value)),
    { initialValue: '' }
  )

  // Sync the city value signal with selectedCitySignal
  effect(() => {
    const cityValue = cityValueSignal()
    selectedCitySignal.set(cityValue || '')
  })

  // Fetch cities when postal code changes
  effect(async () => {
    const postalCode = postalCodeSignal()
    if (untracked(() => lockedFields().city)) return
    if (postalCode.length === 5) {
      cityLookupCompletedPostalCodeSignal.set('')

      const cities = await firstValueFrom(
        locationService.getCitiesByPostalCode(postalCode).pipe(
          catchError(() => of([])),
          // we only need the city names
          map((cities: City[]) => cities.map((city) => city.ortsname)),
          // filter distinct values
          map((names: string[]) => Array.from(new Set(names))),
          // sort alphabetically
          map((names: string[]) => names.sort((a, b) => a.localeCompare(b)))
        )
      )
      citiesSignal.set(cities)
      cityLookupCompletedPostalCodeSignal.set(postalCode)
    } else {
      // Clear cities when postal code is invalid
      citiesSignal.set([])
      cityLookupCompletedPostalCodeSignal.set('')
      formGroup.controls.addressCity.setValue('')
    }
  })

  // Auto-set city when there's exactly one option
  effect(() => {
    const cities = citiesSignal()
    if (untracked(() => lockedFields().city)) return
    const pending = untracked(() => pendingExternalValues())
    if (cities.length === 0) {
      formGroup.controls.addressCity.disable()
      selectedCitySignal.set('')
      isCityReadonly.set(false)
    } else if (pending?.city && cities.includes(pending.city)) {
      formGroup.controls.addressCity.enable()
      formGroup.controls.addressCity.setValue(pending.city)
      selectedCitySignal.set(pending.city)
      isCityReadonly.set(false)
    } else if (cities.length === 1) {
      formGroup.controls.addressCity.disable()
      formGroup.controls.addressCity.setValue(cities[0])
      selectedCitySignal.set(cities[0])
      // Make it readonly so user can't edit the auto-filled value
      isCityReadonly.set(true)
    } else if (cities.length > 1) {
      formGroup.controls.addressCity.setValue('')
      selectedCitySignal.set('')
      formGroup.controls.addressCity.enable()
      isCityReadonly.set(false)
    }
  })

  // Fetch streets when city signal changes
  effect(async () => {
    const city = selectedCitySignal()
    const postalCode = postalCodeSignal()
    if (untracked(() => lockedFields().street)) return

    if (city && postalCode.length === 5) {
      const streets = await firstValueFrom(
        locationService.getStreetsByCity(postalCode, city).pipe(
          catchError(() => of([])),
          // we only need the street names
          map((streets: Street[]) => streets.map((street) => street.strasse)),
          // filter distinct values
          map((names: string[]) => Array.from(new Set(names))),
          // sort alphabetically
          map((names: string[]) => names.sort((a, b) => a.localeCompare(b)))
        )
      )
      streetsSignal.set(streets)
    } else {
      // Clear streets when city is not selected
      streetsSignal.set([])
      formGroup.controls.addressStreet.setValue('')
      formGroup.controls.addressHouseNumber.setValue('')
    }
  })

  // Auto-set street when there's exactly one option
  effect(() => {
    const streets = streetsSignal()
    if (untracked(() => lockedFields().street)) return
    const pending = untracked(() => pendingExternalValues())
    if (pending?.street && streets.includes(pending.street)) {
      formGroup.controls.addressStreet.enable()
      formGroup.controls.addressStreet.setValue(pending.street)
      isStreetReadonly.set(false)
      untracked(() => pendingExternalValues.set(null))
    } else if (streets.length === 1) {
      formGroup.controls.addressStreet.disable()
      formGroup.controls.addressStreet.setValue(streets[0])
      // Make it readonly so user can't edit the auto-filled value
      isStreetReadonly.set(true)
    } else if (streets.length > 1) {
      // Clear street value when multiple options are available
      formGroup.controls.addressStreet.setValue('')
      formGroup.controls.addressStreet.enable()
      isStreetReadonly.set(false)
    } else {
      // Disable when no streets available
      formGroup.controls.addressStreet.disable()
      isStreetReadonly.set(false)
    }
  })

  //ensure that the inputed by user streetName exists in the streets list
  effect(() => {
    if (lockedFields().street) return
    if (!streetsSignal()?.includes(streetValueSignal() as string)) {
      formGroup.controls.addressStreet.setErrors({ invalidStreet: true })
    }
  })

  // Clear streets and house number when city changes
  effect(() => {
    citiesSignal()
    // Reset streets and house number when cities change
    if (untracked(() => hasAnyLockedField())) return
    streetsSignal.set([])
    formGroup.controls.addressStreet.setValue('')
    formGroup.controls.addressHouseNumber.setValue('')
  })

  // Postal code input handler
  const onPostalCodeInput = (event: Event) => {
    const digitsOnly = inputRestrictions.restrictPostalCodeInput(event)

    // Update the form control with the filtered value
    formGroup.controls.addressPostalCode.setValue(digitsOnly)
    postalCodeSignal.set(digitsOnly)
  }

  const resetFetchedData = () => {
    formGroup.controls.contractOwnerName.setValue('', { emitEvent: false })
    resetFullAddress()
  }

  // Applies Commercial Register values in one synchronous pass and locks only the fields that came back filled
  const applyFetchedAddress = (address: { postalCode?: string; city?: string; street?: string }) => {
    const postalCode = (address.postalCode ?? '').trim()
    const city = (address.city ?? '').trim()
    const street = (address.street ?? '').trim()

    lockedFields.set({
      postalCode: !!postalCode,
      city: !!city,
      street: !!street,
      houseNumber: lockedFields().houseNumber,
    })

    if (postalCode) {
      formGroup.controls.addressPostalCode.setValue(postalCode, { emitEvent: false })
      cityLookupCompletedPostalCodeSignal.set(postalCode)
    }

    if (city) {
      citiesSignal.set([city])
      selectedCitySignal.set(city)
      formGroup.controls.addressCity.setValue(city, { emitEvent: false })
      formGroup.controls.addressCity.disable({ emitEvent: false })
      isCityReadonly.set(true)
    }

    if (street) {
      streetsSignal.set([street])
      formGroup.controls.addressStreet.setValue(street, { emitEvent: false })
      formGroup.controls.addressStreet.setErrors(null)
      formGroup.controls.addressStreet.disable({ emitEvent: false })
      isStreetReadonly.set(true)
    }

    // Set last so unlocked fields still trigger the normal lookups for the values that were not delivered
    postalCodeSignal.set(postalCode)
  }

  const setHouseNumberLock = (isLocked: boolean) => lockedFields.set({ ...lockedFields(), houseNumber: isLocked })

  const clearFieldLocks = () => lockedFields.set({ postalCode: false, city: false, street: false, houseNumber: false })

  // The reset icon writes to the inner control only, so clear the dependent fields explicitly
  const resetFullAddress = () => {
    lockedFields.set({ postalCode: false, city: false, street: false, houseNumber: false })
    postalCodeSignal.set('')
    citiesSignal.set([])
    cityLookupCompletedPostalCodeSignal.set('')
    selectedCitySignal.set('')
    streetsSignal.set([])
    formGroup.controls.addressPostalCode.setValue('')
    formGroup.controls.addressPostalCode.markAsUntouched()
    formGroup.controls.addressCity.setValue('')
    formGroup.controls.addressStreet.setValue('')
    formGroup.controls.addressHouseNumber.setValue('')
    formGroup.controls.addressHouseNumber.markAsUntouched()
  }

  return {
    // Signals
    postalCodeSignal,
    citiesSignal,
    streetsSignal,
    selectedCitySignal,

    // Readonly state signals
    isCityReadonly,
    isStreetReadonly,

    // Computed values
    shouldShowCityDropdown,
    shouldShowCityInput,
    shouldShowStreetDropdown,
    shouldShowStreetInput,

    // Methods
    onPostalCodeInput,
    resetFullAddress,
    resetFetchedData,
    applyFetchedAddress,
    setHouseNumberLock,
    clearFieldLocks,
    setPrefilledAddress: (city: string, street: string) => pendingExternalValues.set({ city, street }),
    clearPrefilledAddress: () => pendingExternalValues.set(null),
    setFullAddress: (city: string, street: string, postalCode: string) =>
      applyFetchedAddress({ postalCode, city, street }),
  }
}
